import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import SearchableSelect from '../Common/SearchableSelect';
import UniversalModal from '../Common/UniversalModal';
import OrganizerForm from '../Organizers/OrganizerForm';
import Toast from '../Common/Toast';
import { normalizeSnilsDigits, formatSnils, hasManualSnilsDash } from '../../utils/snils';
import { applyPhoneMask, formatPhone, getCaretPosition, normalizePhoneDigits } from '../../utils/phoneMask';
import { parseLocalDateTime, formatLocalDate } from '../../utils/dateTime';
import OwnerBadge from '../Common/OwnerBadge';

const COURT_KIND_ARBITRATION = 'arbitration';
const COURT_KIND_APPEAL = 'appeal';

const detectCourtKind = (value = '') => {
  const lowered = String(value || '').toLowerCase();
  if (lowered.includes('апелляц')) return COURT_KIND_APPEAL;
  return COURT_KIND_ARBITRATION;
};

const _collapseSpaces = (value = '') => {
  return String(value || '').split(/\s+/).filter(Boolean).join(' ');
};

const stripCourtPrefix = (name = '', kind = COURT_KIND_ARBITRATION) => {
  const raw = String(name || '').trim();
  if (!raw) return '';
  let result = raw;
  if (kind === COURT_KIND_APPEAL) {
    result = result.replace(/апелляционный\s+суд/i, '');
  } else {
    result = result.replace(/арбитражный\s+суд/i, '');
  }
  return _collapseSpaces(result);
};

const matchCourtByName = (courts, value) => {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const direct = courts.find((c) => c.name === raw);
  if (direct) return direct;
  return courts.find((c) => {
    const kind = c.court_type === 'appeal' ? COURT_KIND_APPEAL : COURT_KIND_ARBITRATION;
    const stripped = stripCourtPrefix(c.name, kind);
    return stripped === raw;
  }) || null;
};

const DebtorForm = ({ 
  debtor, 
  formData, 
  setFormData, 
  onSave, 
  onCancel, 
  isViewMode = false,
  onFormChange,
  showToast
}) => {
  const [initialFormData, setInitialFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const phoneInputRef = useRef(null);
  const snilsInputRef = useRef(null);
  const innInputRef = useRef(null);
  const [managers, setManagers] = useState([]);
  const [managersError, setManagersError] = useState('');
  const [managerModalState, setManagerModalState] = useState({ isOpen: false, mode: null, item: null });
  const [managerFormData, setManagerFormData] = useState(null);
  const [managerHasChanges, setManagerHasChanges] = useState(false);
  const [innFillStatus, setInnFillStatus] = useState({ loading: false, error: '' });
  const [innFillTaskId, setInnFillTaskId] = useState('');
  const innFillTaskIdRef = useRef('');
  const [innTypePrompt, setInnTypePrompt] = useState({ open: false, inn: '' });
  const [innFillProgress, setInnFillProgress] = useState(0);
  const [innFillTarget, setInnFillTarget] = useState(0);
  const [innFillStage, setInnFillStage] = useState('Подготовка');
  const [innFillEntity, setInnFillEntity] = useState('debtor');
  const innFillEntityRef = useRef('debtor');
  const [showInnFillProgress, setShowInnFillProgress] = useState(false);
  const innFillStreamRef = useRef(null);
  const innFillPollRef = useRef(null);
  const [autoFilledFields, setAutoFilledFields] = useState(() => new Set());
  const dateInputRefs = useRef({});
  const [courts, setCourts] = useState([]);
  const [courtsError, setCourtsError] = useState('');
  const [courtKind, setCourtKind] = useState(COURT_KIND_ARBITRATION);
  const [selectedCourtId, setSelectedCourtId] = useState(null);
  const [caseActCourtKind, setCaseActCourtKind] = useState(COURT_KIND_ARBITRATION);
  const [caseActCourtId, setCaseActCourtId] = useState(null);
  const [managerActCourtKind, setManagerActCourtKind] = useState(COURT_KIND_ARBITRATION);
  const [managerActCourtId, setManagerActCourtId] = useState(null);
  const [caseActCourtTouched, setCaseActCourtTouched] = useState(false);
  const caseActCourtEmptyOnOpenRef = useRef(false);
  const [innGuideActive, setInnGuideActive] = useState(true);
  const [innGuideToast, setInnGuideToast] = useState(null);
  const hasInnValue = Boolean(String(formData?.inn || '').trim());

  const emptyManager = {
    name: '',
    type: 'arbitrage_manager',
    inn: '',
    contacts: '',
    sro_info: '',
    phone: '',
    email: '',
    mailing_address: '',
    snils: ''
  };

  const loadManagers = async () => {
    try {
      setManagersError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getManagers(`?page=${page}&page_size=96`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setManagers(all);
      return all;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setManagersError(`Ошибка при загрузке управляющих: ${msg}`);
      return [];
    }
  };

  useEffect(() => {
    loadManagers().catch(() => {});
  }, []);

  useEffect(() => {
    setInnGuideActive(true);
  }, [debtor?.id]);

  useEffect(() => {
    if (!isViewMode && innGuideActive && !hasInnValue) {
      setInnGuideToast({
        type: 'success',
        message: 'Рекомендуемый порядок: введите ИНН и нажмите «Заполнить по ИНН».'
      });
      return;
    }
    setInnGuideToast(null);
  }, [isViewMode, innGuideActive, hasInnValue]);

  const dismissInnGuide = () => {
    if (innGuideActive) setInnGuideActive(false);
    if (innGuideToast) setInnGuideToast(null);
  };

  const setInnFillEntityValue = (entity) => {
    innFillEntityRef.current = entity;
    setInnFillEntity(entity);
  };

  const stopDebtorInnFillWatchers = () => {
    if (innFillStreamRef.current) {
      innFillStreamRef.current.close();
      innFillStreamRef.current = null;
    }
    if (innFillPollRef.current) {
      clearInterval(innFillPollRef.current);
      innFillPollRef.current = null;
    }
  };

  useEffect(() => {
    if (!innFillStatus.loading) {
      if (showInnFillProgress) {
        setInnFillStage('Готово');
        setInnFillProgress(100);
        setInnFillTarget(100);
        const closeTimer = setTimeout(() => {
          setShowInnFillProgress(false);
          setInnFillProgress(0);
          setInnFillTarget(0);
          setInnFillStage('Подготовка');
          setInnFillEntityValue('debtor');
        }, 150);
        return () => clearTimeout(closeTimer);
      }
      stopDebtorInnFillWatchers();
      setInnFillEntityValue('debtor');
      return;
    }
    let isActive = true;
    setShowInnFillProgress(true);
    setInnFillProgress(0);
    setInnFillStage('Подготовка');
    setInnFillEntityValue('debtor');

    stopDebtorInnFillWatchers();

    const startPolling = () => {
      if (innFillPollRef.current) return;
      const pollOnce = async () => {
        try {
          const taskId = innFillTaskIdRef.current || innFillTaskId;
          if (!taskId) return;
          if (innFillEntityRef.current !== 'debtor') return;
          const resp = await apiService.getDebtorInnFillStatus(taskId);
          if (innFillEntityRef.current !== 'debtor') return;
          const payload = resp?.data || {};
          if (payload.stage) setInnFillStage(payload.stage);
          if (Number.isFinite(payload.percent)) {
            const percent = Math.max(0, Math.min(100, Math.round(payload.percent)));
            setInnFillTarget(percent);
          }
        } catch (_) {}
      };
      pollOnce();
      innFillPollRef.current = setInterval(pollOnce, 700);
    };

    (async () => {
      const token = await apiService.ensureAccessToken();
      if (!token) {
        startPolling();
        return;
      }
      const streamUrl = apiService.getDebtorInnFillStreamUrl(token);
      const es = new EventSource(streamUrl);
      innFillStreamRef.current = es;
      es.onmessage = (event) => {
        if (!isActive) return;
        if (innFillEntityRef.current !== 'debtor') return;
        try {
          const payload = JSON.parse(event.data || '{}');
          const taskId = innFillTaskIdRef.current || innFillTaskId;
          if (!taskId) return;
          if (payload.task_id && payload.task_id !== taskId) return;
          if (payload.stage) setInnFillStage(payload.stage);
          if (Number.isFinite(payload.percent)) {
            const percent = Math.max(0, Math.min(100, Math.round(payload.percent)));
            setInnFillTarget(percent);
          }
        } catch (_) {}
      };
      es.onerror = () => {
        if (innFillStreamRef.current) {
          innFillStreamRef.current.close();
          innFillStreamRef.current = null;
        }
      };
      startPolling();
    })();

    return () => {
      isActive = false;
      stopDebtorInnFillWatchers();
    };
  }, [innFillStatus.loading, innFillTaskId]);

  useEffect(() => {
    if (!innFillStatus.loading) return;
    if (innFillProgress >= innFillTarget) return;
    const timer = setInterval(() => {
      setInnFillProgress((prev) => {
        if (prev >= innFillTarget) return prev;
        return Math.min(prev + 1, innFillTarget);
      });
    }, 120);
    return () => clearInterval(timer);
  }, [innFillStatus.loading, innFillTarget, innFillProgress]);

  const loadCourts = async () => {
    try {
      setCourtsError('');
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getCourts(`?page=${page}`);
        const chunk = resp.data?.results || resp.data || [];
        all.push(...chunk);
        if (!resp.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setCourts(all);
      return all;
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setCourtsError(`Ошибка при загрузке судов: ${msg}`);
      return [];
    }
  };

  useEffect(() => {
    loadCourts().catch(() => {});
  }, []);

  useEffect(() => {
    const currentName = formData?.arbitration_court || '';
    if (!currentName) {
      setSelectedCourtId(null);
      return;
    }
    const match = matchCourtByName(courts, currentName);
    if (match) {
      setSelectedCourtId(match.id);
      setCourtKind(match.court_type === 'appeal' ? COURT_KIND_APPEAL : COURT_KIND_ARBITRATION);
      return;
    }
    setCourtKind(detectCourtKind(currentName));
    setSelectedCourtId(null);
  }, [formData?.arbitration_court, courts]);

  useEffect(() => {
    const currentName = formData?.case_act_court || '';
    if (!currentName) {
      setCaseActCourtId(null);
      return;
    }
    const match = matchCourtByName(courts, currentName);
    if (match) {
      setCaseActCourtId(match.id);
      setCaseActCourtKind(match.court_type === 'appeal' ? COURT_KIND_APPEAL : COURT_KIND_ARBITRATION);
      return;
    }
    setCaseActCourtKind(detectCourtKind(currentName));
    setCaseActCourtId(null);
  }, [formData?.case_act_court, courts]);

  useEffect(() => {
    const currentName = formData?.manager_act_court || '';
    if (!currentName) {
      setManagerActCourtId(null);
      return;
    }
    const match = matchCourtByName(courts, currentName);
    if (match) {
      setManagerActCourtId(match.id);
      setManagerActCourtKind(match.court_type === 'appeal' ? COURT_KIND_APPEAL : COURT_KIND_ARBITRATION);
      return;
    }
    setManagerActCourtKind(detectCourtKind(currentName));
    setManagerActCourtId(null);
  }, [formData?.manager_act_court, courts]);

  useEffect(() => {
    if (!formData) return;
    if (!caseActCourtTouched && caseActCourtEmptyOnOpenRef.current && formData.arbitration_court) {
      if (formData.case_act_court !== formData.arbitration_court) {
        setFormData((prev) => ({ ...(prev || {}), case_act_court: formData.arbitration_court }));
      }
    }
    if (!formData.manager_act_independent) {
      const target = formData.arbitration_court || '';
      if (formData.manager_act_court !== target) {
        setFormData((prev) => ({ ...(prev || {}), manager_act_court: target }));
      }
      if (formData.manager_act_type !== formData.case_act_type) {
        setFormData((prev) => ({ ...(prev || {}), manager_act_type: prev.case_act_type || 'decision' }));
      }
    }
  }, [formData?.case_act_court, formData?.arbitration_court, formData?.manager_act_independent, formData?.manager_act_type, formData?.case_act_type, caseActCourtTouched]);

  // Инициализация формы
  useEffect(() => {
    let newFormData;

    if (debtor) {
      const debtorType = debtor.type || 'legal';
      const existingSnilsDigits = normalizeSnilsDigits(debtor.snils || '');
      const existingSnilsFormatted = debtorType === 'individual'
        ? formatSnils(existingSnilsDigits, hasManualSnilsDash(debtor.snils || ''))
        : (debtor.snils || '');
      const defaultProcedure = debtorType === 'legal' ? 'competition' : 'citizen_property_realization';
      newFormData = {
        ...debtor,
        type: debtorType,
        legal_form: debtorType === 'entrepreneur' ? 'ИП' : (debtor.legal_form || ''),
        procedure: debtor.procedure || defaultProcedure,
        phone: formatPhone(normalizePhoneDigits(debtor.phone || '')),
        snils: existingSnilsFormatted,
        case_act_date: formatLocalDate(debtor.case_act_date || '') || (debtor.case_act_date || ''),
        case_act_type: debtor.case_act_type || 'decision',
        case_act_court: debtor.case_act_court || debtor.arbitration_court || '',
        manager_act_date: debtor.manager_act_independent
          ? (formatLocalDate(debtor.manager_act_date || '') || (debtor.manager_act_date || ''))
          : (formatLocalDate(debtor.manager_act_date || '') || formatLocalDate(debtor.case_act_date || '') || (debtor.manager_act_date || debtor.case_act_date || '')),
        manager_act_independent: !!debtor.manager_act_independent
        ,
        manager_act_type: debtor.manager_act_independent
          ? (debtor.manager_act_type || debtor.case_act_type || 'decision')
          : (debtor.case_act_type || debtor.manager_act_type || 'decision'),
        arbitration_manager: debtor.arbitration_manager || '',
        manager_act_court: debtor.manager_act_court || debtor.arbitration_court || ''
      };
    } else {
      newFormData = {
        type: 'legal',
        name: '',
        legal_form: '',
        inn: '',
        kpp: '',
        ogrn: '',
        snils: '',
        address: '',
        email: '',
        phone: '',
        case_number: '',
        arbitration_court: '',
        procedure: 'competition',
        case_act_date: '',
        case_act_type: 'decision',
        case_act_court: '',
        manager_act_date: '',
        manager_act_independent: false,
        manager_act_type: 'decision',
        arbitration_manager: '',
        manager_act_court: ''
      };
    }

    setFormData(newFormData);
    caseActCourtEmptyOnOpenRef.current = !(newFormData?.case_act_court || '');
    setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
    setValidationErrors({});
    setCourtKind(detectCourtKind(newFormData?.arbitration_court || ''));
    setCaseActCourtKind(detectCourtKind(newFormData?.case_act_court || newFormData?.arbitration_court || ''));
    setManagerActCourtKind(detectCourtKind(newFormData?.manager_act_court || newFormData?.arbitration_court || ''));
    setSelectedCourtId(null);
    setCaseActCourtId(null);
    setManagerActCourtId(null);
    setCaseActCourtTouched(false);
  }, [debtor, setFormData]);

  // Отслеживание изменений в форме
  useEffect(() => {
    if (initialFormData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      if (onFormChange) {
        onFormChange(changed);
      }
    }
  }, [formData, initialFormData, onFormChange]);

  function sanitizeDigits(value, maxLength) {
    return value.replace(/\D/g, '').slice(0, maxLength);
  }

  const handlePhoneChange = (event) => {
    if (isViewMode) return;
    const { value, selectionStart } = event.target;
    const { formatted, caret } = applyPhoneMask(value, selectionStart);

    setFormData(prev => ({ ...(prev || {}), phone: formatted }));
    requestAnimationFrame(() => {
      const input = phoneInputRef.current;
      if (!input) return;
      input.setSelectionRange(caret, caret);
    });
  }

  const handleSnilsChange = (event) => {
    if (isViewMode) return;
    const { value, selectionStart } = event.target;
    const digits = normalizeSnilsDigits(value);
    const useDashAfterNine = hasManualSnilsDash(value);
    const formatted = formatSnils(digits, useDashAfterNine);
    const digitsBeforeCaret = normalizeSnilsDigits(value.slice(0, selectionStart ?? value.length));
    const digitsBeforeCaretCount = digitsBeforeCaret.length;
    setFormData(prev => ({ ...(prev || {}), snils: formatted }));
    requestAnimationFrame(() => {
      if (!snilsInputRef.current) return;
      let caret = getCaretPosition(formatted, digitsBeforeCaretCount, formatted.length);
      if (
        useDashAfterNine &&
        digits.length === digitsBeforeCaretCount &&
        formatted.endsWith('-')
      ) {
        caret = formatted.length;
      }
      snilsInputRef.current.setSelectionRange(caret, caret);
    });
  };

  const getInnLength = () => 12;

  const applyTypeChange = (prev, value) => {
    if (!prev) return prev;
    const updated = { ...prev, type: value };
    if (value === 'entrepreneur') {
      updated.legal_form = 'ИП';
      updated.kpp = '';
    } else if (value !== 'legal') {
      updated.legal_form = '';
      updated.kpp = '';
    }
    if (value === 'individual') {
      updated.ogrn = '';
    }
    if (value !== 'individual') {
      updated.snils = '';
    } else {
      const existingDigits = normalizeSnilsDigits(updated.snils || '');
      updated.snils = formatSnils(existingDigits, hasManualSnilsDash(updated.snils || ''));
    }
    updated.phone = formatPhone(normalizePhoneDigits(updated.phone || ''));
    updated.inn = sanitizeDigits(updated.inn || '', getInnLength(value));
    updated.ogrn = sanitizeDigits(updated.ogrn || '', getOgrnLength(value));
    return updated;
  };

  const getOgrnLength = (type) => {
    if (type === 'entrepreneur') return 15;
    return 13;
  };

  const handleCourtKindChange = (event) => {
    if (isViewMode) return;
    const next = event.target.value;
    setCourtKind(next);
    setSelectedCourtId(null);
    updateFormData('arbitration_court', '');
  };

  const handleCourtSelect = (courtId) => {
    if (isViewMode) return;
    setSelectedCourtId(courtId);
    const selected = courts.find((c) => String(c.id) === String(courtId));
    const nextName = selected?.name
      ? (courtKind === COURT_KIND_APPEAL ? selected.name : stripCourtPrefix(selected.name, courtKind))
      : '';
    updateFormData('arbitration_court', nextName);
    setValidationErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next.arbitration_court;
      return next;
    });
  };

  const handleCaseActCourtKindChange = (event) => {
    if (isViewMode) return;
    const next = event.target.value;
    setCaseActCourtTouched(true);
    setCaseActCourtKind(next);
    setCaseActCourtId(null);
    updateFormData('case_act_court', '');
  };

  const handleCaseActCourtSelect = (courtId) => {
    if (isViewMode) return;
    setCaseActCourtTouched(true);
    setCaseActCourtId(courtId);
    const selected = courts.find((c) => String(c.id) === String(courtId));
    const nextName = selected?.name
      ? (caseActCourtKind === COURT_KIND_APPEAL ? selected.name : stripCourtPrefix(selected.name, caseActCourtKind))
      : '';
    updateFormData('case_act_court', nextName);
    setValidationErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next.case_act_court;
      return next;
    });
  };

  const handleManagerActCourtKindChange = (event) => {
    if (isViewMode || !formData?.manager_act_independent) return;
    const next = event.target.value;
    setManagerActCourtKind(next);
    setManagerActCourtId(null);
    updateFormData('manager_act_court', '');
  };

  const handleManagerActCourtSelect = (courtId) => {
    if (isViewMode || !formData?.manager_act_independent) return;
    setManagerActCourtId(courtId);
    const selected = courts.find((c) => String(c.id) === String(courtId));
    const nextName = selected?.name
      ? (managerActCourtKind === COURT_KIND_APPEAL ? selected.name : stripCourtPrefix(selected.name, managerActCourtKind))
      : '';
    updateFormData('manager_act_court', nextName);
    setValidationErrors((prev) => {
      const next = { ...(prev || {}) };
      delete next.manager_act_court;
      return next;
    });
  };
  const updateFormData = (field, value) => {
    if (!formData) return;
    if (autoFilledFields.has(field)) {
      setAutoFilledFields((prev) => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
    }

    if (field === 'inn' && innFillStatus.error) {
      setInnFillStatus({ loading: false, error: '' });
    }

    if (field === 'case_act_type') {
      setFormData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          case_act_type: value,
          manager_act_type: prev.manager_act_independent ? (prev.manager_act_type || 'decision') : value
        };
      });
      return;
    }

    if (field === 'manager_act_type') {
      if (!formData.manager_act_independent) {
        return;
      }
      setFormData({ ...formData, manager_act_type: value });
      return;
    }

    if (field === 'case_act_date') {
      const normalizedValue = value;
      setFormData(prev => {
        if (!prev) return prev;
        const shouldSyncManager = !prev.manager_act_independent;
        return {
          ...prev,
          case_act_date: normalizedValue,
          manager_act_date: shouldSyncManager ? (normalizedValue || '') : (prev.manager_act_date || '')
        };
      });
      return;
    }

    if (field === 'manager_act_date') {
      if (!formData.manager_act_independent) {
        return;
      }
      setFormData({ ...formData, manager_act_date: value });
      return;
    }

    if (field === 'type') {
      setFormData(applyTypeChange(formData, value));
      return;
    }

    if (field === 'kpp' && formData.type !== 'legal') {
      return;
    }

    let nextValue = value;
    switch (field) {
      case 'inn':
        nextValue = sanitizeDigits(value, getInnLength(formData.type || 'legal'));
        break;
      case 'kpp':
        nextValue = sanitizeDigits(value, 9);
        break;
      case 'ogrn':
        nextValue = sanitizeDigits(value, getOgrnLength(formData.type || 'legal'));
        break;
      case 'snils':
        nextValue = formatSnils(normalizeSnilsDigits(value), hasManualSnilsDash(value));
        break;
      case 'phone':
        nextValue = formatPhone(normalizePhoneDigits(value));
        break;
      default:
        break;
    }

    setFormData({ ...formData, [field]: nextValue });
  };

  const validateForm = () => {
    const errors = {};
    const currentType = formData?.type || 'legal';

    if (!formData?.name || !formData.name.trim()) {
      errors.name = 'Обязательное поле';
    }
    if (!formData?.case_number || !String(formData.case_number).trim()) {
      errors.case_number = 'Обязательное поле';
    }

    const cleanINN = (formData?.inn || '').replace(/\D/g, '');
    if (cleanINN) {
      if (!(cleanINN.length === 10 || cleanINN.length === 12)) {
        errors.inn = 'ИНН должен содержать 10 или 12 цифр';
      }
      if (currentType === 'legal' && cleanINN.length === 12) {
        errors.inn = 'ИНН должника превышает длину для юридического лица';
      }
      if ((currentType === 'individual' || currentType === 'entrepreneur') && cleanINN.length === 10) {
        errors.inn = `ИНН должника недостаточной длины для ${currentType === 'individual' ? 'физического лица' : 'ИП'}`;
      }
    }

    const cleanKPP = (formData?.kpp || '').replace(/\D/g, '');
    if (currentType === 'legal' && cleanKPP && cleanKPP.length !== 9) {
      errors.kpp = 'КПП должен содержать 9 цифр';
    }

    const cleanOGRN = (formData?.ogrn || '').replace(/\D/g, '');
    if (cleanOGRN && !(cleanOGRN.length === 13 || cleanOGRN.length === 15)) {
      errors.ogrn = 'ОГРН должен содержать 13 или 15 цифр';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Неверный формат email';
    }

    const cleanPhone = (formData?.phone || '').replace(/\D/g, '');
    if (formData?.phone && cleanPhone.length < 10) {
      errors.phone = 'Номер телефона должен содержать не менее 10 цифр';
    }

    if (!formData?.arbitration_court) {
      errors.arbitration_court = 'Укажите арбитражный суд';
    }

    if (!formData?.case_act_court) {
      errors.case_act_court = 'Укажите суд';
    }

    if (formData?.manager_act_independent && !formData?.manager_act_court) {
      errors.manager_act_court = 'Укажите суд';
    }

    if (!formData?.arbitration_manager) {
      errors.arbitration_manager = 'Выберите арбитражного управляющего';
    }

    return errors;
  };

  const openCreateManager = () => {
    setManagerFormData(emptyManager);
    setManagerHasChanges(false);
    setManagerModalState({ isOpen: true, mode: 'create', item: null });
  };

  const openEditManager = async (managerId) => {
    if (!managerId) return;
    const existing = managers.find((m) => String(m.id) === String(managerId));
    if (existing) {
      setManagerFormData({
        ...emptyManager,
        ...existing,
        type: 'arbitrage_manager'
      });
      setManagerHasChanges(false);
      setManagerModalState({ isOpen: true, mode: 'edit', item: existing });
      return;
    }

    try {
      const resp = await apiService.getManager(managerId);
      const fetched = resp.data;
      setManagers((prev) => [fetched, ...(prev || [])]);
      setManagerFormData({
        ...emptyManager,
        ...fetched,
        type: 'arbitrage_manager'
      });
      setManagerHasChanges(false);
      setManagerModalState({ isOpen: true, mode: 'edit', item: fetched });
    } catch (_) {
      // Если не удалось — просто откроем создание
      openCreateManager();
    }
  };

  const closeManagerModal = () => {
    setManagerModalState({ isOpen: false, mode: null, item: null });
    setManagerFormData(null);
    setManagerHasChanges(false);
  };

  const handleSaveManager = async (payload, { exit } = {}) => {
    try {
      const data = { ...(payload || {}), type: 'arbitrage_manager' };
      let saved = null;
      if (managerModalState.item?.id) {
        const resp = await apiService.updateManager(managerModalState.item.id, data);
        saved = resp.data || { ...managerModalState.item, ...data };
        setManagers((prev) => (prev || []).map((m) => (m.id === saved.id ? saved : m)));
      } else {
        const resp = await apiService.createManager(data);
        saved = resp.data;
        setManagers((prev) => [saved, ...(prev || [])]);
      }

      // Привязываем управляющего к должнику сразу после сохранения
      setFormData((prev) => ({ ...(prev || {}), arbitration_manager: saved.id }));
      setValidationErrors((prev) => {
        const next = { ...(prev || {}) };
        delete next.arbitration_manager;
        return next;
      });

      setManagerHasChanges(false);

      if (exit) {
        closeManagerModal();
      } else {
        setManagerModalState({ isOpen: true, mode: 'edit', item: saved });
      }

      return saved;
    } catch (err) {
      console.error('Ошибка при сохранении управляющего:', err);
      const data = err?.response?.data;
      let message = '';
      if (typeof data === 'string') {
        message = data;
      } else if (data?.detail) {
        message = data.detail;
      } else if (data?.inn) {
        message = Array.isArray(data.inn) ? data.inn.join(', ') : String(data.inn);
      } else if (data?.name) {
        message = Array.isArray(data.name) ? data.name.join(', ') : String(data.name);
      } else if (data?.non_field_errors) {
        message = Array.isArray(data.non_field_errors)
          ? data.non_field_errors.join(', ')
          : String(data.non_field_errors);
      } else if (data?.error) {
        message = String(data.error);
      } else {
        message = err?.message || 'Не удалось сохранить арбитражного управляющего.';
      }
      setManagersError(message);
      if (typeof showToast === 'function') {
        showToast(message, 'error');
      }
      return false;
    }
  };

  const runSave = async (shouldExit = false) => {
    if (isViewMode || isSaving) return false;
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      if (errors.inn && innInputRef.current) {
        innInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        innInputRef.current.focus();
      }
      return false;
    }

    if (!onSave) return false;

    setIsSaving(true);
    try {
      const toIsoDate = (value) => {
        if (!value) return '';
        const d = parseLocalDateTime(value);
        if (!d) return value;
        const pad = (v) => String(v).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      };
      const resolveCourtName = (courtId, rawValue) => {
        const raw = String(rawValue || '').trim();
        if (courtId) {
          const found = courts.find((c) => String(c.id) === String(courtId));
          if (found?.name) return found.name;
        }
        const matched = matchCourtByName(courts, raw);
        if (matched?.name) return matched.name;
        return raw;
      };
      const arbitrationCourtValue = formData?.arbitration_court || '';
      const resolvedCaseActCourt = resolveCourtName(caseActCourtId, formData?.case_act_court || arbitrationCourtValue);
      const resolvedManagerActCourt = resolveCourtName(managerActCourtId, formData?.manager_act_court || '');
      const caseActCourtValue = resolvedCaseActCourt || arbitrationCourtValue;
      const managerActCourtValue = formData?.manager_act_independent
        ? (resolvedManagerActCourt || formData?.manager_act_court || '')
        : arbitrationCourtValue;
      const payload = {
        ...(formData || {}),
        case_act_court: caseActCourtValue,
        manager_act_court: managerActCourtValue,
        case_act_date: toIsoDate(formData?.case_act_date || ''),
        manager_act_date: toIsoDate(formData?.manager_act_date || ''),
        manager_act_type: formData?.manager_act_independent
          ? (formData?.manager_act_type || formData?.case_act_type || 'decision')
          : (formData?.case_act_type || 'decision')
      };
      const result = await onSave(payload, { exit: shouldExit });

      if (result === false) {
        return false;
      }

      const snapshot = JSON.parse(JSON.stringify(payload));
      if (snapshot.case_act_date) {
        snapshot.case_act_date = formatLocalDate(snapshot.case_act_date) || snapshot.case_act_date;
      }
      if (snapshot.manager_act_date) {
        snapshot.manager_act_date = formatLocalDate(snapshot.manager_act_date) || snapshot.manager_act_date;
      }
      if (setFormData) {
        setFormData(snapshot);
      }
      setInitialFormData(snapshot);
      setValidationErrors({});
      if (onFormChange) {
        onFormChange(false);
      }
      return result;
    } catch (error) {
      console.error('Ошибка при сохранении должника:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSave(false).catch(() => {});
  };

  const toIsoDateString = (value) => {
    const d = parseLocalDateTime(value);
    if (!d) return '';
    const pad = (v) => String(v).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };

  const formatDateInput = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    if (!digits) return '';
    const dd = digits.slice(0, 2);
    const mm = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);
    let out = dd;
    if (digits.length >= 3) out += `.${mm}`;
    if (digits.length >= 5) out += `.${yyyy}`;
    return out;
  };

  const handleManagerActIndependentToggle = (event) => {
    if (isViewMode) return;
    const checked = event.target.checked;
    setFormData(prev => {
      if (!prev) return prev;
      const syncedDate = checked
        ? (prev.manager_act_date || prev.case_act_date || '')
        : (prev.case_act_date || '');
      const syncedCourt = checked
        ? (prev.manager_act_court || prev.arbitration_court || '')
        : (prev.arbitration_court || '');
      return {
        ...prev,
        manager_act_independent: checked,
        manager_act_date: syncedDate,
        manager_act_court: syncedCourt,
        manager_act_type: checked
          ? (prev.manager_act_type || prev.case_act_type || 'decision')
          : (prev.case_act_type || 'decision')
      };
    });
  };

  // Функции валидации
  const validateINN = (inn) => {
    if (!inn) return true;
    const cleanINN = inn.replace(/\D/g, '');
    return cleanINN.length === 10 || cleanINN.length === 12;
  };

  const pollInnResult = async (taskId) => {
    const startAt = Date.now();
    const maxWaitMs = 8 * 60 * 1000;
    while (Date.now() - startAt < maxWaitMs) {
      let resp;
      try {
        resp = await apiService.getDebtorInnFillResult(taskId);
      } catch (err) {
        const pending = err.response?.status === 202;
        if (!pending) {
          throw err;
        }
      }
      const payload = resp?.data || {};
      if (payload.status === 'ready') {
        const data = payload.data || {};
        const hasContent = Object.values(data || {}).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).trim() !== '';
        });
        if (!hasContent) {
          throw new Error('Такой ИНН не найден');
        }
        return data;
      }
      if (payload.status === 'error') {
        const rawMessage = payload.error || 'Не удалось заполнить данные по ИНН.';
        const message = /не\\s+найден|ничего\\s+не\\s+найдено/i.test(rawMessage)
          ? 'Такой ИНН не найден'
          : rawMessage;
        throw new Error(message);
      }
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    throw new Error('Импорт занял слишком много времени. Попробуйте позже.');
  };

  const pollManagerInnResult = async (taskId) => {
    const startAt = Date.now();
    const maxWaitMs = 8 * 60 * 1000;
    while (Date.now() - startAt < maxWaitMs) {
      let resp;
      try {
        const statusResp = await apiService.getManagerInnFillStatus(taskId);
        const statusPayload = statusResp?.data || {};
        if (statusPayload.stage) {
          setInnFillStage(`Управляющий: ${statusPayload.stage}`);
        }
        if (Number.isFinite(statusPayload.percent)) {
          const percent = Math.max(0, Math.min(100, Math.round(statusPayload.percent)));
          setInnFillTarget(percent);
        }
        resp = await apiService.getManagerInnFillResult(taskId);
      } catch (err) {
        const pending = err.response?.status === 202;
        if (!pending) {
          throw err;
        }
      }
      const payload = resp?.data || {};
      if (payload.status === 'ready') {
        const data = payload.data || {};
        const hasContent = Object.values(data || {}).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).trim() !== '';
        });
        if (!hasContent) {
          throw new Error('Управляющий с таким ИНН не найден');
        }
        return data;
      }
      if (payload.status === 'error') {
        throw new Error(payload.error || 'Не удалось заполнить управляющего по ИНН.');
      }
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    throw new Error('Импорт управляющего занял слишком много времени. Попробуйте позже.');
  };

  const findManagerByInn = (items, innDigits) => {
    return (items || []).find((manager) => {
      const itemInn = String(manager?.inn || '').replace(/\D/g, '');
      return itemInn && itemInn === innDigits;
    }) || null;
  };

  const resolveManagerByInn = async (managerInn) => {
    const managerInnDigits = String(managerInn || '').replace(/\D/g, '');
    if (!managerInnDigits || !(managerInnDigits.length === 10 || managerInnDigits.length === 12)) {
      return null;
    }

    setInnFillEntityValue('manager');
    stopDebtorInnFillWatchers();
    setInnFillStage('Управляющий: проверяем существующую карточку');
    setInnFillProgress(0);
    setInnFillTarget(0);
    const loadedManagers = await loadManagers();
    const existing = findManagerByInn(loadedManagers, managerInnDigits);
    if (existing?.id) {
      setFormData((prev) => ({ ...(prev || {}), arbitration_manager: existing.id }));
      setValidationErrors((prev) => {
        const next = { ...(prev || {}) };
        delete next.arbitration_manager;
        return next;
      });
      setInnFillStage('Управляющий: найдена существующая карточка');
      setInnFillTarget(100);
      return existing;
    }

    setInnFillStage('Управляющий: запускаем заполнение по ИНН');
    setInnFillProgress(0);
    setInnFillTarget(0);
    const response = await apiService.fillManagerByInn({ inn: managerInnDigits });
    const taskId = response?.data?.task_id;
    if (!taskId) {
      throw new Error('Не удалось запустить импорт управляющего по ИНН.');
    }
    const data = await pollManagerInnResult(taskId);
    const phoneDigits = normalizePhoneDigits(data.phone || '');
    const formattedPhone = phoneDigits ? formatPhone(phoneDigits) : '';
    const snilsDigits = normalizeSnilsDigits(data.snils || '');
    const managerPayload = {
      type: 'arbitrage_manager',
      entity_type: 'individual',
      name: data.name || `Управляющий ИНН ${managerInnDigits}`,
      inn: managerInnDigits,
      contacts: '',
      snils: snilsDigits ? formatSnils(snilsDigits, hasManualSnilsDash(data.snils || '')) : '',
      email: data.email || '',
      phone: formattedPhone,
      mailing_address: data.mailing_address || '',
      sro_info: data.sro_name || '',
      sro_name: data.sro_name || '',
      sro_inn: data.sro_inn || '',
      sro_ogrn: data.sro_ogrn || '',
      sro_address: data.sro_address || ''
    };

    setInnFillStage('Управляющий: создаем карточку');
    try {
      const createResp = await apiService.createManager(managerPayload);
      const saved = createResp.data;
      setManagers((prev) => [saved, ...(prev || [])]);
      setFormData((prev) => ({ ...(prev || {}), arbitration_manager: saved.id }));
      setValidationErrors((prev) => {
        const next = { ...(prev || {}) };
        delete next.arbitration_manager;
        return next;
      });
      setInnFillStage('Управляющий: карточка заполнена');
      setInnFillTarget(100);
      return saved;
    } catch (err) {
      const duplicate = err?.response?.data?.inn || err?.response?.data?.non_field_errors;
      if (duplicate) {
        const refreshed = await loadManagers();
        const duplicateExisting = findManagerByInn(refreshed, managerInnDigits);
        if (duplicateExisting?.id) {
          setFormData((prev) => ({ ...(prev || {}), arbitration_manager: duplicateExisting.id }));
          setValidationErrors((prev) => {
            const next = { ...(prev || {}) };
            delete next.arbitration_manager;
            return next;
          });
          setInnFillStage('Управляющий: найдена существующая карточка');
          setInnFillTarget(100);
          return duplicateExisting;
        }
      }
      throw err;
    }
  };

  const runFillByInn = async (debtorTypeOverride) => {
    const innValue = formData?.inn || '';
    const cleanInn = innValue.replace(/\D/g, '');
    const debtorType = debtorTypeOverride || currentType || 'legal';
    setInnFillEntityValue('debtor');
    setInnFillStatus({ loading: true, error: '' });
    try {
      const response = await apiService.fillDebtorByInn({
        inn: cleanInn,
        debtor_type: debtorType
      });
      const taskId = response?.data?.task_id;
      if (!taskId) {
        throw new Error('Не удалось запустить импорт по ИНН.');
      }
      innFillTaskIdRef.current = taskId;
      setInnFillTaskId(taskId);
      const data = await pollInnResult(taskId);
      const phoneDigits = normalizePhoneDigits(data.phone || '');
      const formattedPhone = phoneDigits ? formatPhone(phoneDigits) : '';
      const nextAutoFilled = new Set();
      const markIfValue = (key, value) => {
        if (value !== undefined && value !== null && String(value).trim() !== '') {
          nextAutoFilled.add(key);
        }
      };
      markIfValue('name', data.name);
      markIfValue('address', data.address);
      markIfValue('ogrn', data.ogrn);
      markIfValue('kpp', data.kpp);
      markIfValue('email', data.email);
      markIfValue('phone', formattedPhone);
      markIfValue('snils', data.snils);
      markIfValue('legal_form', data.legal_form);
      markIfValue('case_number', data.case_number);
      markIfValue('arbitration_court', data.arbitration_court);
      markIfValue('case_act_court', data.case_act_court);
      markIfValue('case_act_date', data.case_act_date);
      // manager_act_date derives from case_act_date when not independent; do not mark as imported
      markIfValue('case_act_type', data.case_act_type);
      // manager_act_type derives from case_act_type when not independent; do not mark as imported
      markIfValue('procedure', data.procedure);
      markIfValue('arbitration_manager', data.manager_inn);
      setFormData(prev => ({
        ...(prev || {}),
        name: data.name || prev?.name || '',
        address: data.address || prev?.address || '',
        ogrn: data.ogrn || prev?.ogrn || '',
        kpp: debtorType === 'legal' ? (data.kpp || prev?.kpp || '') : (prev?.kpp || ''),
        email: data.email || prev?.email || '',
        phone: formattedPhone || prev?.phone || '',
        snils: data.snils
          ? formatSnils(normalizeSnilsDigits(data.snils), hasManualSnilsDash(data.snils))
          : (prev?.snils || ''),
        legal_form: debtorType === 'legal'
          ? (data.legal_form || prev?.legal_form || '')
          : (debtorType === 'entrepreneur' ? 'ИП' : (prev?.legal_form || '')),
        case_number: data.case_number || prev?.case_number || '',
        arbitration_court: data.arbitration_court || prev?.arbitration_court || '',
        case_act_court: data.case_act_court || prev?.case_act_court || '',
        case_act_date: formatLocalDate(data.case_act_date || '') || data.case_act_date || prev?.case_act_date || '',
        manager_act_date: prev?.manager_act_independent
          ? (prev?.manager_act_date || '')
          : (formatLocalDate(data.case_act_date || '') || data.case_act_date || prev?.manager_act_date || prev?.case_act_date || ''),
        case_act_type: data.case_act_type || prev?.case_act_type || 'decision',
        manager_act_type: prev?.manager_act_independent
          ? (prev?.manager_act_type || prev?.case_act_type || data.case_act_type || 'decision')
          : (data.case_act_type || prev?.case_act_type || prev?.manager_act_type || 'decision'),
        procedure: data.procedure || prev?.procedure || ''
      }));
      if (data.manager_inn) {
        try {
          await resolveManagerByInn(data.manager_inn);
        } catch (managerErr) {
          const managerMessage = managerErr.response?.data?.error
            || managerErr.response?.data?.detail
            || managerErr.message
            || 'Не удалось заполнить управляющего по ИНН.';
          setInnFillStatus({
            loading: false,
            error: `Данные должника заполнены, но управляющего не удалось заполнить: ${managerMessage}`
          });
          return;
        }
      }
      if (nextAutoFilled.size > 0) {
        setAutoFilledFields((prev) => {
          const merged = new Set(prev);
          nextAutoFilled.forEach((key) => merged.add(key));
          return merged;
        });
      }
    } catch (err) {
      const rawMessage = err.response?.data?.error || err.message || 'Не удалось заполнить данные по ИНН.';
      const message = /не\\s+найден|ничего\\s+не\\s+найдено/i.test(rawMessage)
        ? 'Такой ИНН не найден'
        : rawMessage;
      setInnFillStatus({ loading: false, error: message });
      return;
    }
    setInnFillStatus({ loading: false, error: '' });
  };

  const handleFillByInn = async () => {
    if (isViewMode || innFillStatus.loading) return;
    const innValue = formData?.inn || '';
    const cleanInn = innValue.replace(/\D/g, '');
    if (!validateINN(cleanInn)) {
      setInnFillStatus({ loading: false, error: 'Укажите корректный ИНН.' });
      return;
    }

    if ((currentType || 'legal') === 'legal' && cleanInn.length === 12) {
      setInnTypePrompt({ open: true, inn: cleanInn });
      return;
    }

    if ((currentType === 'individual' || currentType === 'entrepreneur') && cleanInn.length === 10) {
      setFormData((prev) => applyTypeChange(prev, 'legal'));
      await runFillByInn('legal');
      return;
    }

    await runFillByInn(currentType || 'legal');
  };

  const CHECKBOX_HEIGHT = 18;
  const shouldDeemphasizeSecondaryFields = !isViewMode && !hasInnValue;
  const shouldHighlightInn = !isViewMode && innGuideActive && !hasInnValue;

  useEffect(() => {
    if (!shouldHighlightInn) return;
    const timer = setTimeout(() => {
      if (innInputRef.current && document.activeElement !== innInputRef.current) {
        innInputRef.current.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [shouldHighlightInn]);

  const renderField = ({
    label,
    name,
    type = 'text',
    required = false,
    options = [],
    placeholder,
    rows,
    disabled = false,
    wrapperStyle,
    autoFilled = false,
    labelStyle: labelStyleOverrides
  }) => {
    const error = validationErrors[name];
    const isDisabled = isViewMode || disabled;
    const { labelWhiteSpace, marginBottom, ...restWrapperStyle } = wrapperStyle || {};
    const fieldStyle = {
      marginBottom: marginBottom !== undefined ? marginBottom : '20px',
      ...restWrapperStyle
    };

    const labelStyle = {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: isDisabled ? '#6c757d' : '#2c3e50',
      fontSize: '14px',
      whiteSpace: labelWhiteSpace || 'normal',
      ...(labelStyleOverrides || {})
    };

    const inputStyle = {
      width: '100%',
      padding: '12px',
      border: error ? '2px solid #e74c3c' : '1px solid #ddd',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: isDisabled ? '#f1f3f5' : 'white',
      color: isDisabled ? '#6c757d' : '#2c3e50',
      outline: 'none',
      cursor: isDisabled ? 'not-allowed' : 'text',
      transition: 'border-color 0.2s ease',
      boxShadow: autoFilled ? 'inset 4px 0 0 #1f6feb' : 'none'
    };
    if (type === 'select' && ['case_act_type', 'manager_act_type'].includes(name)) {
      inputStyle.fontSize = '16px';
      inputStyle.padding = '10px 12px';
      inputStyle.minHeight = '40px';
    }
    if (type === 'select' && name === 'legal_form') {
      inputStyle.height = '42px';
    }
    if (name === 'kpp') {
      inputStyle.height = '44px';
    }

    const errorStyle = {
      color: '#e74c3c',
      fontSize: '12px',
      marginTop: '4px',
      display: 'block'
    };

    return (
      <div
        style={{
          ...fieldStyle,
          ...wrapperStyle,
          opacity: shouldDeemphasizeSecondaryFields && name !== 'inn' ? 0.62 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: '#e74c3c' }}>*</span>}
        </label>
        
        {type === 'select' ? (
          <select
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={inputStyle}
            required={required}
            disabled={isDisabled}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={{ ...inputStyle, minHeight: rows ? `${rows * 20 + 24}px` : '80px', resize: 'vertical' }}
            placeholder={placeholder}
            required={required}
            disabled={isDisabled}
          />
        ) : type === 'date' ? (
          (() => {
            const ref = dateInputRefs.current[name] || (dateInputRefs.current[name] = React.createRef());
            const isoValue = toIsoDateString(formData[name] || '');
            const openPicker = () => {
              if (isDisabled) return;
              const el = ref.current;
              if (!el) return;
              if (typeof el.showPicker === 'function') {
                el.showPicker();
              } else {
                el.focus();
                el.click();
              }
            };
            return (
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  value={formData[name] || ''}
                  onChange={(e) => updateFormData(name, formatDateInput(e.target.value))}
                  style={{ ...inputStyle, paddingRight: '42px' }}
                  placeholder="ДД.ММ.ГГГГ"
                  required={required}
                  disabled={isDisabled}
                />
                <button
                  type="button"
                  onClick={openPicker}
                  disabled={isDisabled}
                  title="Открыть календарь"
                  style={{
                    position: 'absolute',
                    right: '6px',
                    width: '32px',
                    height: '32px',
                    border: '1px solid #dee2e6',
                    borderRadius: '6px',
                    background: isDisabled ? '#f1f3f5' : '#fff',
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  📅
                </button>
                <input
                  ref={ref}
                  type="date"
                  value={isoValue}
                  onChange={(e) => {
                    const next = e.target.value;
                    const formatted = formatLocalDate(next) || '';
                    updateFormData(name, formatted);
                  }}
                  style={{
                    position: 'absolute',
                    opacity: 0,
                    pointerEvents: 'none',
                    width: 0,
                    height: 0
                  }}
                />
              </div>
            );
          })()
        ) : (
          <input
            type={type === 'date' ? 'text' : type}
            value={type === 'date' ? (formData[name] || '') : (formData[name] || '')}
            onChange={(!isViewMode && name === 'phone') ? handlePhoneChange : (!isViewMode && name === 'snils') ? handleSnilsChange : (e) => updateFormData(name, e.target.value)}
            ref={!isViewMode && name === 'phone' ? phoneInputRef : (!isViewMode && name === 'snils' ? snilsInputRef : undefined)}
            inputMode={name === 'phone' ? 'tel' : (name === 'snils' ? 'numeric' : undefined)}
            autoComplete={name === 'phone' ? 'tel' : undefined}
            style={inputStyle}
            placeholder={type === 'date' ? 'ДД.ММ.ГГГГ' : placeholder}
            required={required}
            disabled={isDisabled}
          />
        )}
        
        {error && <span style={errorStyle}>{error}</span>}
      </div>
    );
  };

  const currentType = formData?.type || 'legal';
  const isLegal = currentType === 'legal';
  const isEntrepreneur = currentType === 'entrepreneur';
  const procedureOptions = isLegal
    ? [
        { value: 'observation', label: 'наблюдения' },
        { value: 'competition', label: 'конкурсного производства' },
        { value: 'financial_rehabilitation', label: 'финансового оздоровления' },
        { value: 'external_management', label: 'внешнего управления' }
      ]
    : [
        { value: 'citizen_debt_restructuring', label: 'реструктуризации долгов гражданина' },
        { value: 'citizen_property_realization', label: 'реализации имущества гражданина' }
      ];
  const defaultProcedure = isLegal ? 'competition' : 'citizen_property_realization';
  const typeOptions = [
    { value: 'legal', label: 'Юридическое лицо' },
    { value: 'entrepreneur', label: 'Индивидуальный предприниматель' },
    { value: 'individual', label: 'Физическое лицо' }
  ];

  const nameLabel = isLegal ? 'Название' : 'ФИО';
  const namePlaceholder = isLegal
    ? 'Название организации без указания организационно-правовой формы'
    : isEntrepreneur
      ? 'ФИО индивидуального предпринимателя'
      : 'ФИО должника';

  const innPlaceholder = isLegal ? '1234567890' : '123456789012';
  const ogrnPlaceholder = isLegal ? '1234567890123' : '123456789012345';

  // При смене типа должника подставляем дефолтную процедуру, если текущая не подходит
  useEffect(() => {
    const current = formData?.procedure || '';
    const allowed = procedureOptions.some(o => o.value === current);
    if (!allowed) {
      updateFormData('procedure', defaultProcedure);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentType]);

  useEffect(() => {
    if (currentType === 'entrepreneur' && formData?.legal_form !== 'ИП') {
      updateFormData('legal_form', 'ИП');
      return;
    }
    if (currentType === 'individual' && formData?.legal_form) {
      updateFormData('legal_form', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentType, formData?.legal_form]);

  const procedureValue = formData?.procedure || defaultProcedure;
  const MANAGER_ROLE_LABELS_BY_PROCEDURE = {
    observation: 'Временный управляющий',
    competition: 'Конкурсный управляющий',
    financial_rehabilitation: 'Административный управляющий',
    external_management: 'Внешний управляющий',
    citizen_debt_restructuring: 'Финансовый управляющий',
    citizen_property_realization: 'Финансовый управляющий'
  };
  const managerLabel = MANAGER_ROLE_LABELS_BY_PROCEDURE[procedureValue] || 'Арбитражный управляющий';

  return (
    <form
      onSubmit={handleSubmit}
      onChangeCapture={() => {
        dismissInnGuide();
      }}
      onInputCapture={dismissInnGuide}
      onMouseDownCapture={dismissInnGuide}
      onKeyDownCapture={dismissInnGuide}
      style={{ position: 'relative' }}
    >
      <Toast toast={innGuideToast} />
      {!!debtor && (
        <div style={{ marginBottom: 14 }}>
          <OwnerBadge email={debtor.owner_email} />
        </div>
      )}
      {renderField({
        label: 'Тип должника',
        name: 'type',
        type: 'select',
        required: true,
        options: typeOptions
      })}

        {(isLegal || isEntrepreneur) ? (
          <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 2.3fr', gap: '16px' }}>
            {isLegal
              ? renderField({
                  label: 'Организационно-правовая форма',
                  name: 'legal_form',
                  type: 'select',
                  autoFilled: autoFilledFields.has('legal_form'),
                  options: [
                    { value: '', label: '—' },
                    { value: 'ООО', label: 'ООО' },
                    { value: 'АО', label: 'АО' },
                    { value: 'ПАО', label: 'ПАО' },
                    { value: 'НАО', label: 'НАО' },
                    { value: 'ОАО', label: 'ОАО' },
                    { value: 'ЗАО', label: 'ЗАО' },
                    { value: 'ПАО/ОАО', label: 'ПАО/ОАО' },
                    { value: 'ГУП', label: 'ГУП' },
                    { value: 'МУП', label: 'МУП' },
                    { value: 'ФГУП', label: 'ФГУП' },
                    { value: 'ГБУ', label: 'ГБУ' },
                    { value: 'МБУ', label: 'МБУ' },
                    { value: 'ПК', label: 'ПК' },
                    { value: 'ЖСК', label: 'ЖСК' },
                    { value: 'ТСЖ', label: 'ТСЖ' },
                    { value: 'НКО', label: 'НКО' },
                    { value: 'АНО', label: 'АНО' },
                    { value: 'Фонд', label: 'Фонд' },
                    { value: 'Ассоциация', label: 'Ассоциация' },
                    { value: 'Союз', label: 'Союз' }
                  ]
                })
              : renderField({
                  label: 'Организационно-правовая форма',
                  name: 'legal_form',
                  type: 'text',
                  disabled: true
                })}
      {renderField({
        label: nameLabel,
        name: 'name',
        required: true,
        placeholder: namePlaceholder,
        autoFilled: autoFilledFields.has('name')
      })}
        </div>
      ) : (
        renderField({
          label: nameLabel,
          name: 'name',
          required: true,
          placeholder: namePlaceholder,
          autoFilled: autoFilledFields.has('name')
        })
      )}

      <div style={{ display: 'grid', gridTemplateColumns: isLegal ? '1fr 1fr' : '1fr', gap: '16px' }}>
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: isViewMode ? '#6c757d' : '#2c3e50', fontSize: '14px' }}>
            ИНН
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: isViewMode ? '1fr' : '1fr auto', alignItems: 'center', gap: '8px' }}>
            <input
              type="text"
              value={formData.inn || ''}
              onChange={(e) => updateFormData('inn', e.target.value)}
              ref={!isViewMode ? innInputRef : undefined}
              style={{
                width: '100%',
                padding: '12px',
                border: validationErrors.inn ? '2px solid #e74c3c' : '1px solid #ddd',
                borderColor: shouldHighlightInn ? '#4da3ff' : (validationErrors.inn ? '#e74c3c' : '#ddd'),
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: isViewMode ? '#f1f3f5' : 'white',
                color: isViewMode ? '#6c757d' : '#2c3e50',
                outline: 'none',
                cursor: isViewMode ? 'not-allowed' : 'text',
                transition: 'border-color 0.2s ease',
                height: 44,
                boxShadow: shouldHighlightInn ? '0 0 0 3px rgba(77, 163, 255, 0.18)' : 'none',
              }}
              placeholder={innPlaceholder}
              disabled={isViewMode}
            />
            {!isViewMode && (
              <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={handleFillByInn}
                    disabled={innFillStatus.loading || !validateINN(formData?.inn || '')}
                    style={{
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 6,
                      border: '1px solid #0d6efd',
                      background: innFillStatus.loading ? '#cfe2ff' : '#0d6efd',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: innFillStatus.loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      boxShadow: innFillStatus.loading ? 'none' : '0 2px 6px rgba(13, 110, 253, 0.25)',
                    }}
                  >
                    {innFillStatus.loading ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 50 50"
                          role="img"
                          aria-label="Загрузка"
                        >
                          <circle
                            cx="25"
                            cy="25"
                            r="20"
                            fill="none"
                            stroke="#6c757d"
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray="90 60"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 25 25"
                              to="360 25 25"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        </svg>
                        Заполнение...
                      </span>
                    ) : (
                      'Заполнить по ИНН'
                    )}
                  </button>
                )}
                {innTypePrompt.open && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 6,
                      background: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      padding: '10px',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                      zIndex: 10,
                      minWidth: 220
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 8 }}>
                      Для кого выполнить заполнение?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #ced4da',
                          background: '#f8f9fa',
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          setInnTypePrompt({ open: false, inn: '' });
                          setFormData((prev) => applyTypeChange(prev, 'individual'));
                          await runFillByInn('individual');
                        }}
                      >
                        Физлицо
                      </button>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #ced4da',
                          background: '#f8f9fa',
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          setInnTypePrompt({ open: false, inn: '' });
                          setFormData((prev) => applyTypeChange(prev, 'entrepreneur'));
                          await runFillByInn('entrepreneur');
                        }}
                      >
                        ИП
                      </button>
                    </div>
                    <button
                      type="button"
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid transparent',
                        background: 'transparent',
                        fontSize: 12,
                        color: '#6c757d',
                        cursor: 'pointer'
                      }}
                      onClick={() => setInnTypePrompt({ open: false, inn: '' })}
                    >
                      Отмена
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {validationErrors.inn && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.inn}
            </span>
          )}
          {innFillStatus.error && (
            <span style={{ color: '#e74c3c', fontSize: 12, marginTop: '4px', display: 'block' }}>
              {innFillStatus.error}
            </span>
          )}
        </div>

        {isLegal && renderField({
          label: 'КПП',
          name: 'kpp',
          placeholder: '123456789',
          autoFilled: autoFilledFields.has('kpp')
        })}
      </div>

      {(isLegal || isEntrepreneur) && renderField({
        label: isLegal ? 'ОГРН' : 'ОГРНИП',
        name: 'ogrn',
        placeholder: ogrnPlaceholder,
        autoFilled: autoFilledFields.has('ogrn')
      })}

      {(isEntrepreneur || currentType === 'individual') && renderField({
        label: 'СНИЛС',
        name: 'snils',
        placeholder: 'XXX-XXX-XXX XX',
        autoFilled: autoFilledFields.has('snils')
      })}

      {renderField({
        label: 'Адрес',
        name: 'address',
        type: 'textarea',
        rows: 2,
        placeholder: 'Адрес регистрации',
        autoFilled: autoFilledFields.has('address')
      })}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {renderField({
          label: 'Email',
          name: 'email',
          type: 'email',
          placeholder: 'example@mail.ru',
          autoFilled: autoFilledFields.has('email')
        })}

        {renderField({
          label: 'Телефон',
          name: 'phone',
          placeholder: '+7 (___) ___-__-__',
          autoFilled: autoFilledFields.has('phone')
        })}
      </div>

      {(() => {
        const sectionStyle = {
          border: '1px solid #e1e5e9',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          backgroundColor: '#f8f9fb'
        };
        const sectionTitleStyle = {
          fontSize: '15px',
          fontWeight: '600',
          color: '#2c3e50',
          marginBottom: '16px'
        };
        const labelStyle = {
          display: 'block',
          marginBottom: '8px',
          fontWeight: '500',
          color: '#2c3e50',
          fontSize: '14px'
        };
        const error = validationErrors.arbitration_court;
        const filteredCourts = courts.filter((c) => {
          if (courtKind === COURT_KIND_APPEAL) return c.court_type === 'appeal';
          return c.court_type === 'district' || c.court_type === 'region';
        });
        const filteredCaseActCourts = courts.filter((c) => {
          if (caseActCourtKind === COURT_KIND_APPEAL) return c.court_type === 'appeal';
          return c.court_type === 'district' || c.court_type === 'region';
        });
        const filteredManagerActCourts = courts.filter((c) => {
          if (managerActCourtKind === COURT_KIND_APPEAL) return c.court_type === 'appeal';
          return c.court_type === 'district' || c.court_type === 'region';
        });
        const errorStyle = {
          color: '#e74c3c',
          fontSize: '12px',
          marginTop: '4px',
          display: 'block'
        };
        const managerActIndependent = !!formData?.manager_act_independent;
        const caseActTypeOptions = [
          { value: 'decision', label: 'Решение' },
          { value: 'determination', label: 'Определение' },
          { value: 'decree', label: 'Постановление' }
        ];
        const caseActCourtError = validationErrors.case_act_court;
        const managerActCourtError = validationErrors.manager_act_court;
        return (
          <>
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Сведения о деле о банкротстве</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px minmax(280px, 1fr)',
                  gap: '16px',
                  alignItems: 'flex-end'
                }}
              >
                {renderField({
                  label: 'Номер дела',
                  name: 'case_number',
                  required: true,
                  placeholder: 'А40-12345/2024',
                  wrapperStyle: { minWidth: '170px', maxWidth: '190px', marginBottom: 0, labelWhiteSpace: 'nowrap' },
                  autoFilled: autoFilledFields.has('case_number')
                })}

                <div>
                  <label style={labelStyle}>
                    Арбитражный суд
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: courtKind === COURT_KIND_APPEAL ? '1fr 220px' : '220px 1fr',
                      gap: '0px',
                      alignItems: 'center',
                      boxShadow: autoFilledFields.has('arbitration_court') ? 'inset 4px 0 0 #1f6feb' : 'none',
                      borderRadius: 6
                    }}
                  >
                  {courtKind === COURT_KIND_APPEAL ? (
                    <>
                      <div style={{ minWidth: 0 }}>
                        <SearchableSelect
                          value={selectedCourtId}
                          onChange={handleCourtSelect}
                          options={filteredCourts}
                          placeholder="Выберите суд..."
                          searchPlaceholder="Поиск по названию..."
                          displayKey="name"
                          valueKey="id"
                          formatSelected={(opt) => stripCourtPrefix(opt?.name || '', courtKind)}
                          fallbackText={stripCourtPrefix(formData?.arbitration_court || '', courtKind)}
                          disabled={isViewMode}
                          required={!isViewMode}
                          showValidation={false}
                        />
                      </div>
                      <select
                        value={courtKind}
                        onChange={handleCourtKindChange}
                        disabled={true}
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '16px',
                          backgroundColor: '#f1f3f5',
                          color: '#6c757d'
                        }}
                      >
                        <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                        <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                      </select>
                    </>
                  ) : (
                    <>
                      <select
                        value={courtKind}
                        onChange={handleCourtKindChange}
                        disabled={true}
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          fontSize: '16px',
                          backgroundColor: '#f1f3f5',
                          color: '#6c757d'
                        }}
                      >
                        <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                        <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                      </select>
                      <div style={{ minWidth: 0 }}>
                        <SearchableSelect
                          value={selectedCourtId}
                          onChange={handleCourtSelect}
                          options={filteredCourts}
                          placeholder="Выберите суд..."
                          searchPlaceholder="Поиск по названию..."
                          displayKey="name"
                          valueKey="id"
                          formatSelected={(opt) => stripCourtPrefix(opt?.name || '', courtKind)}
                          fallbackText={stripCourtPrefix(formData?.arbitration_court || '', courtKind)}
                          disabled={isViewMode}
                          required={!isViewMode}
                          showValidation={false}
                        />
                      </div>
                    </>
                  )}
                </div>
                {error && <span style={errorStyle}>{error}</span>}
                {courtsError && <span style={errorStyle}>{courtsError}</span>}
              </div>
            </div>

            <div style={{ borderTop: '1px dashed #d0d7de', margin: '12px 0' }} />

            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Судебные данные</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(120px, 140px) minmax(200px, 240px) minmax(260px, 1fr)',
                  columnGap: '16px',
                  rowGap: '16px',
                  alignItems: 'end'
                }}
              >
                {renderField({
                  label: (
                    <>
                      Дата судебного акта о
                      <br />
                      введении процедуры
                    </>
                  ),
                  name: 'case_act_date',
                  type: 'date',
                  required: true,
                  autoFilled: autoFilledFields.has('case_act_date'),
                  wrapperStyle: {
                    marginBottom: 0,
                    minWidth: '120px',
                    maxWidth: '140px',
                    display: 'grid',
                    gridTemplateRows: 'auto auto'
                  }
                })}

                {renderField({
                  label: 'Тип судебного акта',
                  name: 'case_act_type',
                  type: 'select',
                  options: caseActTypeOptions,
                  autoFilled: autoFilledFields.has('case_act_type'),
                  wrapperStyle: { marginBottom: 0, minWidth: '200px', maxWidth: '240px' }
                })}

                <div>
                  <label style={labelStyle}>
                    Суд, вынесший судебный акт о введении процедуры
                  </label>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: caseActCourtKind === COURT_KIND_APPEAL ? '1fr 220px' : '220px 1fr',
                      gap: '0px',
                      alignItems: 'center',
                      boxShadow: autoFilledFields.has('case_act_court') ? 'inset 4px 0 0 #1f6feb' : 'none',
                      borderRadius: 6
                    }}
                  >
                    {caseActCourtKind === COURT_KIND_APPEAL ? (
                      <>
                        <div style={{ minWidth: 0 }}>
                          <SearchableSelect
                            value={caseActCourtId}
                            onChange={handleCaseActCourtSelect}
                            options={filteredCaseActCourts}
                            placeholder="Выберите суд..."
                            searchPlaceholder="Поиск по названию..."
                            displayKey="name"
                            valueKey="id"
                            formatSelected={(opt) => stripCourtPrefix(opt?.name || '', caseActCourtKind)}
                            fallbackText={stripCourtPrefix(formData?.case_act_court || '', caseActCourtKind)}
                            disabled={isViewMode}
                            showValidation={!isViewMode}
                          />
                        </div>
                        <select
                          value={caseActCourtKind}
                          onChange={handleCaseActCourtKindChange}
                          disabled={isViewMode}
                          style={{
                            padding: '10px 12px',
                            border: caseActCourtError ? '2px solid #e74c3c' : '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            backgroundColor: isViewMode ? '#f8f9fa' : 'white',
                            color: isViewMode ? '#6c757d' : '#2c3e50'
                          }}
                        >
                          <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                          <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                        </select>
                      </>
                    ) : (
                      <>
                        <select
                          value={caseActCourtKind}
                          onChange={handleCaseActCourtKindChange}
                          disabled={isViewMode}
                          style={{
                            padding: '10px 12px',
                            border: caseActCourtError ? '2px solid #e74c3c' : '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '16px',
                            backgroundColor: isViewMode ? '#f8f9fa' : 'white',
                            color: isViewMode ? '#6c757d' : '#2c3e50'
                          }}
                        >
                          <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                          <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                        </select>
                        <div style={{ minWidth: 0 }}>
                          <SearchableSelect
                            value={caseActCourtId}
                            onChange={handleCaseActCourtSelect}
                            options={filteredCaseActCourts}
                            placeholder="Выберите суд..."
                            searchPlaceholder="Поиск по названию..."
                            displayKey="name"
                            valueKey="id"
                            formatSelected={(opt) => stripCourtPrefix(opt?.name || '', caseActCourtKind)}
                            fallbackText={stripCourtPrefix(formData?.case_act_court || '', caseActCourtKind)}
                            disabled={isViewMode}
                            showValidation={!isViewMode}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  {caseActCourtError && <span style={errorStyle}>{caseActCourtError}</span>}
                </div>

                <div
                  style={{
                    gridColumn: '1 / -1',
                    padding: '0'
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(120px, 140px) minmax(200px, 240px) minmax(260px, 1fr)',
                      columnGap: '16px',
                      rowGap: '12px',
                      alignItems: 'end'
                    }}
                  >
                    {renderField({
                      label: (
                        <>
                          Дата судебного акта о назначении
                          <br />
                          арбитражного управляющего
                        </>
                      ),
                      name: 'manager_act_date',
                      type: 'date',
                      disabled: !managerActIndependent,
                      autoFilled: autoFilledFields.has('manager_act_date'),
                      wrapperStyle: {
                        marginBottom: 0,
                        minWidth: '120px',
                        maxWidth: '140px'
                      }
                    })}

                    {renderField({
                      label: 'Тип судебного акта',
                      name: 'manager_act_type',
                      type: 'select',
                      options: caseActTypeOptions,
                      disabled: !managerActIndependent,
                      wrapperStyle: { marginBottom: 0, minWidth: '200px', maxWidth: '240px', alignSelf: 'end' }
                    })}

                    <div style={{ alignSelf: 'end' }}>
                      <label style={labelStyle}>
                        Суд, вынесший судебный акт о назначении арбитражного управляющего
                      </label>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: managerActCourtKind === COURT_KIND_APPEAL ? '1fr 220px' : '220px 1fr',
                          gap: '0px',
                          alignItems: 'center',
                          boxShadow: autoFilledFields.has('manager_act_court') ? 'inset 4px 0 0 #1f6feb' : 'none',
                          borderRadius: 6
                        }}
                      >
                        {managerActCourtKind === COURT_KIND_APPEAL ? (
                          <>
                            <div style={{ minWidth: 0 }}>
                              <SearchableSelect
                                value={managerActCourtId}
                                onChange={handleManagerActCourtSelect}
                                options={filteredManagerActCourts}
                                placeholder="Выберите суд..."
                                searchPlaceholder="Поиск по названию..."
                                displayKey="name"
                                valueKey="id"
                                formatSelected={(opt) => stripCourtPrefix(opt?.name || '', managerActCourtKind)}
                                fallbackText={stripCourtPrefix(formData?.manager_act_court || '', managerActCourtKind)}
                                disabled={isViewMode || !managerActIndependent}
                                showValidation={!isViewMode}
                              />
                            </div>
                            <select
                              value={managerActCourtKind}
                              onChange={handleManagerActCourtKindChange}
                              disabled={isViewMode || !managerActIndependent}
                              style={{
                                padding: '10px 12px',
                                border: managerActCourtError ? '2px solid #e74c3c' : '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '16px',
                                backgroundColor: isViewMode || !managerActIndependent ? '#f8f9fa' : 'white',
                                color: isViewMode || !managerActIndependent ? '#6c757d' : '#2c3e50'
                              }}
                            >
                              <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                              <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                            </select>
                          </>
                        ) : (
                          <>
                            <select
                              value={managerActCourtKind}
                              onChange={handleManagerActCourtKindChange}
                              disabled={isViewMode || !managerActIndependent}
                              style={{
                                padding: '10px 12px',
                                border: managerActCourtError ? '2px solid #e74c3c' : '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '16px',
                                backgroundColor: isViewMode || !managerActIndependent ? '#f8f9fa' : 'white',
                                color: isViewMode || !managerActIndependent ? '#6c757d' : '#2c3e50'
                              }}
                            >
                              <option value={COURT_KIND_ARBITRATION}>Арбитражный суд</option>
                              <option value={COURT_KIND_APPEAL}>апелляционный суд</option>
                            </select>
                            <div style={{ minWidth: 0 }}>
                              <SearchableSelect
                                value={managerActCourtId}
                                onChange={handleManagerActCourtSelect}
                                options={filteredManagerActCourts}
                                placeholder="Выберите суд..."
                                searchPlaceholder="Поиск по названию..."
                                displayKey="name"
                                valueKey="id"
                                formatSelected={(opt) => stripCourtPrefix(opt?.name || '', managerActCourtKind)}
                                fallbackText={stripCourtPrefix(formData?.manager_act_court || '', managerActCourtKind)}
                                disabled={isViewMode || !managerActIndependent}
                                showValidation={!isViewMode}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {managerActCourtError && <span style={errorStyle}>{managerActCourtError}</span>}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '10px',
                      marginBottom: '14px',
                      paddingTop: '10px',
                      borderTop: '1px dashed #d8dee4'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: '#2c3e50',
                        fontSize: '14px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={managerActIndependent}
                        onChange={handleManagerActIndependentToggle}
                        disabled={isViewMode}
                        style={{ width: '18px', height: `${CHECKBOX_HEIGHT}px`, cursor: isViewMode ? 'default' : 'pointer' }}
                      />
                      <span style={{ color: isViewMode ? '#6c757d' : '#2c3e50', whiteSpace: 'normal', lineHeight: '1.2' }}>
                        Назначен другим судебным актом
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontWeight: 500, fontSize: '14px', color: isViewMode ? '#6c757d' : '#2c3e50', whiteSpace: 'nowrap' }}>
                      Процедура
                    </span>
                    <select
                      value={procedureValue}
                      onChange={(e) => updateFormData('procedure', e.target.value)}
                      disabled={isViewMode}
                      style={{
                        padding: '10px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '16px',
                        fontFamily: 'inherit',
                        backgroundColor: isViewMode ? '#f8f9fa' : 'white',
                        color: isViewMode ? '#6c757d' : '#2c3e50',
                        outline: 'none',
                        boxShadow: autoFilledFields.has('procedure') ? 'inset 4px 0 0 #1f6feb' : 'none'
                      }}
                    >
                      {procedureOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>
                    {managerLabel}
                    <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <SearchableSelect
                        value={formData?.arbitration_manager || ''}
                        onChange={(value) => {
                          if (isViewMode) return;
                          updateFormData('arbitration_manager', value);
                        }}
                        options={managers}
                        placeholder="Выберите управляющего"
                        searchPlaceholder="Поиск управляющего..."
                        displayKey="name"
                        valueKey="id"
                        disabled={isViewMode}
                        required={!isViewMode}
                        showValidation={false}
                        onAddNew={openCreateManager}
                        addNewLabel="Добавить нового управляющего"
                      />
                      {validationErrors.arbitration_manager && (
                        <span style={errorStyle}>{validationErrors.arbitration_manager}</span>
                      )}
                      {managersError && (
                        <span style={{ ...errorStyle, color: '#b71c1c' }}>{managersError}</span>
                      )}
                    </div>

                    {!!formData?.arbitration_manager && !isViewMode && (
                      <button
                        type="button"
                        onClick={() => openEditManager(formData.arbitration_manager)}
                        title="Редактировать управляющего"
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          background: 'white',
                          cursor: 'pointer',
                          height: 44
                        }}
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            </div>
          </>
        );
      })()}

      <UniversalModal
        isOpen={managerModalState.isOpen}
        onClose={closeManagerModal}
        title={managerModalState.item ? 'Редактирование арбитражного управляющего' : 'Создание арбитражного управляющего'}
        hasChanges={managerHasChanges}
      >
        <OrganizerForm
          organizer={managerModalState.item}
          formData={managerFormData || emptyManager}
          setFormData={setManagerFormData}
          onSave={handleSaveManager}
          onCancel={closeManagerModal}
          isViewMode={false}
          onFormChange={setManagerHasChanges}
          fixedType="arbitrage_manager"
        />
      </UniversalModal>

      {!isViewMode && (
        <>
          {autoFilledFields.size > 0 && (
            <div style={{ marginTop: '16px', fontSize: '12px', color: '#6c757d' }}>
              Поля с синей полосой слева заполнены автоматически. Метка снимается при ручном изменении.
            </div>
          )}
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => runSave(false).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            type="button"
            onClick={() => runSave(true).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
          </button>
        </div>
        </>
      )}
      
      {isViewMode && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            Закрыть
          </button>
        </div>
      )}
      {showInnFillProgress && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
        >
          <div
            style={{
              width: 420,
              maxWidth: 'calc(100% - 32px)',
              background: '#fff',
              borderRadius: 12,
              padding: '20px 22px',
              boxShadow: '0 12px 30px rgba(0,0,0,0.18)'
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {innFillEntity === 'manager'
                ? 'Заполнение управляющего по ИНН'
                : 'Заполнение должника по ИНН'}
            </div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 8 }}>
              Идет заполнение… {innFillProgress}%
            </div>
            <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
              Статус: {innFillStage || 'Подготовка'}
            </div>
            <div style={{ height: 8, background: '#e9ecef', borderRadius: 999 }}>
              <div
                style={{
                  height: '100%',
                  width: `${innFillProgress}%`,
                  background: '#2f80ed',
                  borderRadius: 999,
                  transition: 'width 0.35s ease'
                }}
              />
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#6c757d' }}>
              Это может занять до минуты.
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default DebtorForm;
